import React from "react";
import MarketingLayout, { SpaLink } from "./MarketingLayout";

// Prose lives in strings (rendered via {}) rather than JSX text so apostrophes/quotes are not
// an eslint no-unescaped-entities concern and the content stays easy to edit.
const INTRO =
  "You can delete your GearCheckAuto account and its associated data at any time. This page " +
  "explains how, what gets removed, and how to reach us if you need help.";

const IN_APP_STEPS = [
  "Sign in to GearCheckAuto in the app or at gearcheckauto.com.",
  "Open your Profile.",
  'Select "Delete account" and confirm.',
];

const EMAIL_STEPS =
  "If you cannot sign in, email us at support@gearcheckauto.com from the address on your " +
  "account and ask us to delete it. We may need to verify your identity before we act on the " +
  "request.";

const DELETED = [
  "Your account and profile (name, email, phone number, and any address or time zone you added).",
  "For repair shops and mobile mechanics: your shop profile, service area, and links.",
  "Your requests and messages, and any photos, videos, or PDFs you attached.",
  "Your push notification tokens and notification preferences.",
  "Any active subscription billing is canceled.",
];

const RETAINED =
  "Message attachments automatically expire and are deleted about 30 days after upload. After " +
  "you delete your account we remove or anonymize your personal information; some records may be " +
  "retained where required by law or for legitimate business purposes, as described in our " +
  "Privacy Policy.";

export default function DeleteAccountPage({ onNavigate }) {
  return (
    <MarketingLayout onNavigate={onNavigate}>
      <section className="bg-gradient-to-b from-blue-50 dark:from-blue-950/40 to-white px-4 py-16">
        <div className="mx-auto max-w-3xl">
          <h1 className="text-4xl font-bold leading-tight tracking-tight text-slate-900 dark:text-gray-100 md:text-5xl">
            Delete your account
          </h1>
          <p className="mt-5 text-lg leading-relaxed text-slate-600 dark:text-gray-300">{INTRO}</p>
        </div>
      </section>

      <article className="mx-auto max-w-3xl space-y-8 px-4 py-12 text-slate-700 dark:text-gray-200">
        <section>
          <h2 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-gray-100">
            Delete your account in the app
          </h2>
          <ol className="mt-3 list-decimal space-y-2 pl-5">
            {IN_APP_STEPS.map((s, i) => (
              <li key={i} className="leading-relaxed">{s}</li>
            ))}
          </ol>
        </section>

        <section>
          <h2 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-gray-100">
            Request deletion by email
          </h2>
          <p className="mt-3 leading-relaxed">{EMAIL_STEPS}</p>
        </section>

        <section>
          <h2 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-gray-100">
            What gets deleted
          </h2>
          <ul className="mt-3 list-disc space-y-2 pl-5">
            {DELETED.map((d, i) => (
              <li key={i} className="leading-relaxed">{d}</li>
            ))}
          </ul>
        </section>

        <section>
          <h2 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-gray-100">
            Data retention
          </h2>
          <p className="mt-3 leading-relaxed">{RETAINED}</p>
        </section>

        <section>
          <h2 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-gray-100">Need help?</h2>
          <p className="mt-3 leading-relaxed">
            Contact us at{" "}
            <a
              href="mailto:support@gearcheckauto.com"
              className="font-semibold text-blue-700 dark:text-blue-300 hover:text-blue-600"
            >
              support@gearcheckauto.com
            </a>{" "}
            or see our{" "}
            <SpaLink
              view="privacy"
              onNavigate={onNavigate}
              className="font-semibold text-blue-700 dark:text-blue-300 hover:text-blue-600"
            >
              Privacy Policy
            </SpaLink>{" "}
            for how we handle your information.
          </p>
        </section>
      </article>
    </MarketingLayout>
  );
}
